import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Breakpoint } from '@travel-frontend/core';
import { Trans, useTranslation } from 'next-i18next';
import { useCallback, useEffect, useState } from 'react';
import { Container } from '../../../../../components/internal/DurationPicker/components/Inputs/components/Container.js';
import { Information } from '../../../../../components/internal/DurationPicker/components/Inputs/components/Information/index.js';
import { MIN_DURATION } from '../../../../../components/internal/DurationPicker/components/Inputs/constants.js';
import { useInputValues } from '../../../../../components/internal/DurationPicker/components/Inputs/hooks/useInputValues.js';
import { isDurationInvalid } from '../../../../../components/internal/DurationPicker/components/Inputs/utils.js';
import { DurationFormatter } from '../../../../../components/SearchForm/components/TravelPeriodField/utils.js';
import { LabelMedium, LabelMediumBold, Notice, ParagraphMediumBold, Radio, Stack, TextField } from '../../../../../primitives/index.js';
import { SPACING_LARGE, SPACING_MEDIUM } from '../../../../../utils/spacingLegacy.js';
export const Inputs = ({
  maxDuration,
  duration,
  onChange,
  setDisable,
  isActivated,
  onDurationIsValid
}) => {
  const {
    t
  } = useTranslation();
  const [selectedInput, setSelectedInput] = useState(duration.max === duration.min ? 'single' : 'range');
  // max and min are number of nights - so we need to format them to days
  const [travelDuration, setTravelDuration] = useState({
    min: duration.min,
    max: duration.max
  });
  const durationIsInvalid = isActivated && travelDuration.max !== null && travelDuration.min !== null && isDurationInvalid(travelDuration, maxDuration);
  const singleDurationIsInvalid = selectedInput === 'single' && durationIsInvalid;
  const rangeDurationIsInvalid = selectedInput === 'range' && durationIsInvalid;
  const getInputValue = useInputValues(selectedInput, isActivated);
  useEffect(() => {
    onDurationIsValid(!durationIsInvalid);
  }, [onDurationIsValid, durationIsInvalid]);
  useEffect(() => {
    // update value as only user activate checkbox
    if (isActivated) {
      const {
        min,
        max
      } = travelDuration;
      if (selectedInput === 'single') {
        onChange({
          min,
          max: min
        });
      } else {
        onChange({
          min,
          max
        });
      }
    }
  }, [selectedInput, isActivated]);
  const errorMessage = t('travelPeriodWidgetNew:inputLabels.error', {
    to: maxDuration
  });
  const infoLabelInRange = _jsx(Trans, {
    i18nKey: 'travelPeriodWidgetNew:inputLabels.infoInRange',
    values: {
      from: DurationFormatter.format(MIN_DURATION),
      to: maxDuration
    },
    components: {
      bold: _jsx(ParagraphMediumBold, {
        as: "span",
        color: "contentSecondary"
      })
    }
  });
  const infoLabel = _jsx(Trans, {
    i18nKey: 'travelPeriodWidgetNew:inputLabels.info',
    values: {
      count: maxDuration
    },
    components: {
      bold: _jsx(ParagraphMediumBold, {
        as: "span",
        color: "contentSecondary"
      })
    }
  });
  const handleTextFieldFocus = field => {
    setSelectedInput(field);
    setDisable('input');
  };
  const handleChange = useCallback(event => {
    const label = event.target.dataset?.label && event.target.dataset?.label || 'min';
    const value = DurationFormatter.parse(event.target.value);
    if (label === 'exact') {
      setTravelDuration({
        max: value,
        min: value
      });
      onChange({
        max: value,
        min: value
      });
    }
    if (label === 'min' || label === 'max') {
      setTravelDuration({
        ...travelDuration,
        [label]: value
      });
      if ((travelDuration?.min || label === 'min') && (travelDuration?.max || label === 'max')) {
        const {
          min,
          max
        } = travelDuration;
        onChange({
          min,
          max,
          [label]: value
        });
      }
    }
  }, [onChange, selectedInput, travelDuration]);
  return _jsxs(Container, {
    spacing: SPACING_LARGE,
    children: [_jsxs(Stack, {
      children: [_jsx(LabelMediumBold, {
        children: t('travelPeriodWidgetNew:inputLabels.headline')
      }), _jsx(Breakpoint, {
        matches: ['xs', 'sm', 'md'],
        children: _jsx(Notice, {
          variant: "info",
          children: selectedInput === 'single' ? infoLabel : infoLabelInRange
        })
      })]
    }), _jsxs(Stack, {
      spacing: singleDurationIsInvalid ? 2.8 : SPACING_MEDIUM,
      children: [_jsxs(Stack, {
        onChange: () => handleTextFieldFocus('single'),
        direction: "row",
        alignItems: "center",
        children: [_jsx(Radio, {
          onChange: () => handleTextFieldFocus('single'),
          checked: selectedInput === 'single' && isActivated
        }), _jsx(TextField, {
          type: 'number',
          min: DurationFormatter.format(MIN_DURATION),
          max: maxDuration,
          onChange: handleChange,
          label: 'Exakte',
          "data-label": 'exact',
          onFocus: () => handleTextFieldFocus('single'),
          size: "small",
          invalid: singleDurationIsInvalid,
          message: singleDurationIsInvalid ? errorMessage : '',
          value: getInputValue('single', travelDuration.min)
        }), _jsx(LabelMedium, {
          children: t('travelPeriodWidgetNew:inputLabels.days')
        }), _jsx(Breakpoint, {
          matches: ['lg', 'xl'],
          children: selectedInput === 'single' && _jsx(Information, {
            children: infoLabel
          })
        })]
      }), _jsxs(Stack, {
        onChange: () => handleTextFieldFocus('range'),
        direction: "row",
        alignItems: "center",
        children: [_jsx(Radio, {
          onChange: () => handleTextFieldFocus('range'),
          checked: selectedInput === 'range' && isActivated
        }), _jsx(TextField, {
          type: 'number',
          min: DurationFormatter.format(MIN_DURATION),
          max: maxDuration,
          onChange: handleChange,
          label: t('travelPeriodWidgetNew:inputLabels.rangeFrom'),
          onFocus: () => handleTextFieldFocus('range'),
          "data-label": 'min',
          size: "small",
          invalid: rangeDurationIsInvalid,
          message: rangeDurationIsInvalid ? errorMessage : '',
          value: getInputValue('range', travelDuration.min)
        }), _jsx(TextField, {
          type: 'number',
          min: DurationFormatter.format(MIN_DURATION),
          max: maxDuration,
          onChange: handleChange,
          "data-label": 'max',
          label: t('travelPeriodWidgetNew:inputLabels.rangeUntil'),
          onFocus: () => handleTextFieldFocus('range'),
          size: "small",
          invalid: rangeDurationIsInvalid,
          value: getInputValue('range', travelDuration.max)
        }), _jsx(LabelMedium, {
          children: t('travelPeriodWidgetNew:inputLabels.days')
        }), _jsx(Breakpoint, {
          matches: ['md', 'lg', 'xl'],
          children: selectedInput === 'range' && _jsx(Information, {
            children: infoLabelInRange
          })
        })]
      })]
    })]
  });
};